import { useState, type FormEvent } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { authClient } from './authClient';
import { isValidEmail } from './authForm';
import {
  EmailField,
  Footer,
  FormError,
  FormStack,
  Note,
  PageHeading,
  RouterLink,
  SubmitButton,
} from './authUi';
import { ACCOUNT_PATHS } from './paths';

/**
 * Ask for a reset link.
 *
 * The confirmation says "if there's an account", and means it: the page reads
 * the same whether or not the address has signed up, so it cannot be used to
 * check who has.
 */

const Address = styled('strong')({ color: BRAND.parchment, wordBreak: 'break-all' });

const TryAgain = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 'inherit',
  fontWeight: 700,
  color: BRAND.parchment,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
});

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [sent, setSent] = useState(false);

  const emailOk = isValidEmail(email);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    setFailure('');
    if (!emailOk) return;

    setBusy(true);
    const result = await authClient.requestPasswordReset(email.trim());
    setBusy(false);
    if (!result.ok) {
      setFailure(result.message);
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <>
        <PageHeading
          title="Check your inbox"
          lede={
            <>
              If there&rsquo;s an account for <Address>{email.trim()}</Address>, we&rsquo;ve sent
              it a link to choose a new password. The link works for one hour.
            </>
          }
        />
        <Note style={{ marginTop: 0 }}>
          Nothing arrived? Check your spam folder, or{' '}
          <TryAgain
            type="button"
            onClick={() => {
              setSent(false);
              setAttempted(false);
            }}
          >
            try a different address
          </TryAgain>
          .
        </Note>
        <Footer>
          <RouterLink to={ACCOUNT_PATHS.logIn}>Back to log in</RouterLink>
        </Footer>
      </>
    );
  }

  return (
    <>
      <PageHeading
        title="Forgot your password?"
        lede="Enter the email you use for My Climate Week and we'll send you a link to choose a new one."
      />

      <FormStack onSubmit={submit} noValidate>
        <EmailField
          value={email}
          onChange={setEmail}
          error={attempted && !emailOk ? 'Enter an email address, like name@example.com' : ''}
        />
        <FormError message={failure} />
        <SubmitButton busy={busy} busyLabel="Sending…">
          Send reset link
        </SubmitButton>
        <Note>
          Signed up with Google or LinkedIn? There&rsquo;s no password to reset — go back and
          continue with that instead.
        </Note>
      </FormStack>

      <Footer>
        Remembered it? <RouterLink to={ACCOUNT_PATHS.logIn}>Back to log in</RouterLink>
      </Footer>
    </>
  );
}
